import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { selectSession } from '@client/model/selectors';
import { useSupportChatAllowed } from '@client/hooks/useSupportChat';
import { openIntercomChat } from '@client/util/intercomUtil';
import { SessionType } from '@shared';
import { Card, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  ShieldX,
  Home as HomeIcon,
  ArrowLeft,
  Mail,
  MessageCircleQuestion,
} from 'lucide-react';

export function UnauthorizedPage() {
  const { sessionType, supportChatWithheld } = useSelector(selectSession);
  const isSupportChatAllowed = useSupportChatAllowed();
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      if (sessionType === SessionType.client) {
        navigate('/client/home');
      } else {
        navigate('/');
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-200 p-4">
      <div className="w-full max-w-lg">
        <Card className="overflow-hidden border-0 shadow-2xl">
          <div className="bg-red-500 px-6 py-8 text-center">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/20">
              <ShieldX className="h-10 w-10 text-white" />
            </div>
            <CardTitle className="mb-2 text-3xl font-bold text-white">
              Access Denied
            </CardTitle>
            <p className="text-red-100">Insufficient permissions</p>
          </div>

          <CardContent className="space-y-6 p-8">
            <div className="text-center">
              <p className="leading-relaxed text-slate-600">
                You don't have the required permissions to view this content.
                Please contact your administrator for assistance.
              </p>
            </div>

            <div className="space-y-3">
              {sessionType === SessionType.client ? (
                <Button
                  asChild
                  className="w-full bg-primary hover:bg-primary-hover"
                  size="lg"
                >
                  <Link to="/client/home">
                    <HomeIcon className="mr-2 h-5 w-5" />
                    Go to Dashboard
                  </Link>
                </Button>
              ) : (
                <Button
                  asChild
                  className="w-full bg-primary hover:bg-primary-hover"
                  size="lg"
                >
                  <Link to="/">
                    <HomeIcon className="mr-2 h-5 w-5" />
                    Return Home
                  </Link>
                </Button>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleGoBack}
                  className="border-slate-300"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Go Back
                </Button>

                {supportChatWithheld ? null : isSupportChatAllowed ? (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={openIntercomChat}
                    className="border-slate-300"
                  >
                    <MessageCircleQuestion className="mr-2 h-4 w-4" />
                    Support
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="lg"
                    asChild
                    className="border-slate-300"
                  >
                    <a href="mailto:hello@joinwaterlily.com">
                      <Mail className="mr-2 h-4 w-4" />
                      Support
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-lg bg-slate-50 p-4 text-center">
              <p className="text-xs text-slate-500">
                Error Code: 403 • Session Type: {sessionType}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
